'use client';

import { useForm } from 'react-hook-form';
import { useState, useEffect } from 'react';
import { useRegionLocale } from 'contexts/RegionLocaleContext';
import { submitBloodVisionFreeRequest } from 'utils/services/blood-vision';
import { useRouter, useSearchParams } from 'next/navigation';
import { Checkmark } from 'assets/icons';
import styles from './CampaignForm.module.css';

interface FormData {
  country: string;
  email: string;
  pincode?: string;
}

interface CampaignFormProps {
  title: string;
  description: string;
  preSelectedCountry?: string;
  disableCountrySelect?: boolean;
  isModal?: boolean;
  preFilledPincode?: string;
  preSelectedPlanId?: number;
  ctaText: string;
  ctaSuccessText: string;
  onCountryChange?: (country: string) => void;
  sourceURL: 'pricing' | 'waitlist';
}

/**
 * CampaignForm - Reusable form component for Blood Vision free campaign
 * Used in both FormSection (full page) and modal display
 */
export const CampaignForm = ({
  title,
  description,
  preSelectedCountry,
  disableCountrySelect = false,
  isModal = false,
  preFilledPincode,
  preSelectedPlanId,
  ctaText,
  ctaSuccessText,
  onCountryChange,
  sourceURL,
}: CampaignFormProps) => {
  const { region: country } = useRegionLocale();
  const router = useRouter();
  const searchParams = useSearchParams();
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<FormData>();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  const selectedCountry = watch('country');
  const email = watch('email');
  const pincode = watch('pincode');

  useEffect(() => {
    // Use preSelectedCountry if provided, otherwise use detected country
    const countryToUse = preSelectedCountry || country;

    if (countryToUse) {
      const allowedCountries = ['IN', 'AE', 'US'];
      const selectedCountry = allowedCountries.includes(countryToUse)
        ? countryToUse
        : 'US';
      setValue('country', selectedCountry);
    }
  }, [country, setValue, preSelectedCountry]);

  // Call onCountryChange when the selected country changes
  useEffect(() => {
    if (selectedCountry && onCountryChange) {
      onCountryChange(selectedCountry);
    }
  }, [selectedCountry, onCountryChange]);

  // Pre-fill pincode if provided (from modal context)
  useEffect(() => {
    if (preFilledPincode) {
      setValue('pincode', preFilledPincode);
    }
  }, [preFilledPincode, setValue]);

  const onSubmit = async (data: FormData) => {
    setIsSubmitting(true);
    setApiError(null);

    // Extract affiliate code and UTM params from URL
    const affiliateCode = searchParams?.get('affiliateCode') || undefined;
    const filteredUtmParams = Array.from(searchParams?.keys() ?? [])
      .filter((key) => key.startsWith('utm_'))
      .reduce((acc, key) => {
        acc[key] = searchParams?.get(key) as string;
        return acc;
      }, {} as Record<string, string>);

    const response = await submitBloodVisionFreeRequest({
      email: data.email,
      country: data.country,
      source_url: sourceURL,
      pincode: data.pincode,
      plan_id: preSelectedPlanId,
      affiliate_code: affiliateCode as string | undefined,
      utm_params:
        Object.keys(filteredUtmParams).length > 0
          ? filteredUtmParams
          : undefined,
    });

    if (response.status === 'ok' && response.data) {
      const campaignStatus = response.data.campaign_status;

      // router.push(
      //   `/order/blood-vision/blood-on-us/?country=${selectedCountry}`,
      // );
      // return;
      if (campaignStatus === 'live') {
        router.push(
          `/order/blood-vision/blood-on-us/?country=${selectedCountry}`,
        );
      } else if (campaignStatus === 'waitlist') {
        setIsSuccess(true);
      } else {
        // undefined or expired
        setApiError('Sorry, the campaign has expired');
      }
    } else {
      setApiError(
        response.error?.message || 'Something went wrong. Please try again.',
      );
    }

    setIsSubmitting(false);
  };

  return (
    <div className={styles.wrapper} data-modal={isModal ? 'true' : undefined}>
      <div className={styles.textContainer}>
        <h2>{title}</h2>
        <p>{description}</p>
      </div>

      <form className={styles.formContainer} onSubmit={handleSubmit(onSubmit)}>
        <select
          {...register('country', { required: 'Country is required' })}
          className={styles.select}
          data-haserror={errors.country ? 'true' : undefined}
          disabled={disableCountrySelect}
          defaultValue=""
        >
          <option value="" disabled>
            Select Country
          </option>
          <option value="IN">India</option>
          <option value="AE">United Arab Emirates</option>
          <option value="US">United States</option>
        </select>

        <input
          {...register('email', {
            required: 'Email is required',
            pattern: {
              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
              message: 'Invalid email address',
            },
          })}
          type="email"
          placeholder="Email address"
          className={styles.input}
          data-haserror={errors.email ? 'true' : undefined}
        />

        {selectedCountry === 'US' && (
          <input
            {...register('pincode', {
              required:
                selectedCountry === 'US' ? 'Zip code is required' : false,
              pattern: {
                value: /^\d{5}(-\d{4})?$/,
                message:
                  'Please enter a valid US zip code (12345 or 12345-6789)',
              },
              validate: {
                validFormat: (value) => {
                  if (!value && selectedCountry !== 'US') return true;
                  if (!value) return 'Zip code is required';

                  // Remove any spaces
                  const cleanValue = value.replace(/\s/g, '');

                  // Check if it's all digits (with optional hyphen)
                  if (!/^\d{5}(-\d{4})?$/.test(cleanValue)) {
                    if (cleanValue.length < 5) {
                      return 'Zip code must be at least 5 digits';
                    }
                    if (!/^\d+(-\d+)?$/.test(cleanValue)) {
                      return 'Zip code must contain only numbers';
                    }
                    if (cleanValue.includes('-')) {
                      const parts = cleanValue.split('-');
                      if (parts[0].length !== 5) {
                        return 'First part must be exactly 5 digits';
                      }
                      if (parts[1] && parts[1].length !== 4) {
                        return 'Extension must be exactly 4 digits (12345-6789)';
                      }
                    } else if (
                      cleanValue.length > 5 &&
                      cleanValue.length !== 9
                    ) {
                      return 'Use format 12345 or 12345-6789';
                    }
                    return 'Invalid zip code format';
                  }

                  // Check for invalid starting digits (00000, etc.)
                  const mainZip = cleanValue.substring(0, 5);
                  if (mainZip === '00000') {
                    return 'Please enter a valid zip code';
                  }

                  return true;
                },
              },
              minLength: {
                value: 5,
                message: 'Zip code must be at least 5 digits',
              },
              maxLength: {
                value: 10,
                message: 'Zip code is too long',
              },
            })}
            type="text"
            placeholder="Zip code (e.g., 90210)"
            className={styles.input}
            data-haserror={errors.pincode ? 'true' : undefined}
            disabled={!!preFilledPincode}
            maxLength={10}
            onInput={(e) => {
              const target = e.target as HTMLInputElement;
              // Allow only digits and hyphen
              target.value = target.value.replace(/[^\d-]/g, '');
              // Prevent multiple hyphens
              target.value = target.value.replace(/-+/g, '-');
              // Prevent hyphen at start
              if (target.value.startsWith('-')) {
                target.value = target.value.substring(1);
              }
            }}
          />
        )}

        <button
          type="submit"
          className={styles.submitButton}
          disabled={
            isSubmitting ||
            isSuccess ||
            !selectedCountry ||
            !email ||
            (selectedCountry === 'US' && !pincode) ||
            !!errors.country ||
            !!errors.email ||
            !!errors.pincode
          }
          data-success={isSuccess ? 'true' : undefined}
        >
          {isSuccess ? (
            <>
              <Checkmark fill="#000000" style={{ marginRight: '4px' }} />{' '}
              {ctaSuccessText}
            </>
          ) : (
            ctaText
          )}
        </button>

        <div
          className={styles.messageContainer}
          data-error={
            errors.country || errors.email || errors.pincode || apiError
              ? 'true'
              : undefined
          }
        >
          {errors.country?.message ||
            errors.email?.message ||
            errors.pincode?.message ||
            apiError}
        </div>

        <p className={styles.disclaimer}>
          Priority access is given on a first-come basis.
        </p>
      </form>
    </div>
  );
};
